<div class="container-fluid h-100">
  <div class="row h-100">
    <div class="col-12 h-100">
      <div class="onboarding-page h-100 d-flex flex-column">
        <div class="page-header mb-4">
          <div class="d-flex align-items-center justify-content-between">
            <h3 class="page-title mb-0">New Customer Onboarding</h3>
            <div class="progress-section w-50">
              <div class="d-flex align-items-center gap-3">
                <div class="progress-info">
                  <div class="d-flex align-items-center gap-2">
                    <span class="progress-label">Progress</span>
                    <span class="badge bg-primary">{{ progressPercentage() }}% Complete</span>
                  </div>
                  <kendo-progressbar
                    [value]="progressPercentage()"
                    [max]="100"
                    class="progress-bar-compact"
                    [label]="{
                      visible: false
                    }"
                  ></kendo-progressbar>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="page-content flex-grow-1 d-flex flex-column">
          <!-- Step Content -->
          <div class="step-content flex-grow-1">
            <!-- Step 1: Customer Information -->
            @if (currentStep() === 'customer-info') {
              <app-customer-info
                [formData]="
                  customerInfoData() || {
                    customerName: '',
                    friendlyName: '',
                    preferredGoLiveDate: null,
                    selectedServices: [],
                    slaLevel: '',
                    shippingContactName: '',
                    shippingContactPhone: '',
                    shippingAddress: '',
                    shippingState: '',
                    shippingCity: '',
                    shippingZipCode: '',
                    additionalLocations: []
                  }
                "
                (dataUpdate)="onCustomerInfoUpdate($event)"
              ></app-customer-info>
            }

            <!-- Service Configuration Steps -->
            @if (currentStep().includes('-config')) {
              <div class="service-config-step">
                @if (currentStep() === 'mxdr-config') {
                  <app-mxdr-service
                    [initialData]="getServiceData('mxdr')"
                    (dataUpdate)="onServiceDataUpdate('mxdr', $event)"
                    (validityUpdate)="onServiceValidityUpdate('mxdr', $event)"
                  ></app-mxdr-service>
                }
                @if (currentStep() === 'medr-config') {
                  <app-medr-service
                    [initialData]="getServiceData('medr')"
                    (dataUpdate)="onServiceDataUpdate('medr', $event)"
                    (validityUpdate)="onServiceValidityUpdate('medr', $event)"
                  ></app-medr-service>
                }
                @if (currentStep() === 'mvss-config') {
                  <app-mvss-service
                    [initialData]="getServiceData('mvss')"
                    (dataUpdate)="onServiceDataUpdate('mvss', $event)"
                    (validityUpdate)="onServiceValidityUpdate('mvss', $event)"
                  ></app-mvss-service>
                }
                @if (currentStep() === 'mes-config') {
                  <app-mes-service
                    [initialData]="getServiceData('mes')"
                    (dataUpdate)="onServiceDataUpdate('mes', $event)"
                    (validityUpdate)="onServiceValidityUpdate('mes', $event)"
                  ></app-mes-service>
                }
                @if (currentStep() === 'professional-services-config') {
                  <app-professional-services
                    [initialData]="getServiceData('professional')"
                    (dataUpdate)="onServiceDataUpdate('professional', $event)"
                    (validityUpdate)="onServiceValidityUpdate('professional', $event)"
                  ></app-professional-services>
                }
              </div>
            }

            <!-- Incident Handling Step -->
            @if (currentStep() === 'incident-handling') {
              <app-incident-handling
                [formData]="formData().incidentHandlingData"
                (dataUpdate)="onIncidentHandlingDataUpdate($event)"
                (validityUpdate)="onIncidentHandlingValidityUpdate($event)"
              ></app-incident-handling>
            }

            <!-- Review & Submit Step -->
            @if (currentStep() === 'review-submit') {
              <div class="review-submit-step">
                <h3 class="mb-3">Review & Submit</h3>
                <div class="review-content">
                  <!-- Review content will be displayed here -->
                  <div class="alert alert-info">
                    <i class="bi bi-info-circle me-2"></i>
                    Please review all information before submitting. You can go back to any step to make changes.
                  </div>

                  <!-- Summary of all data -->
                  <div class="review-summary">
                    <!-- Customer Info Summary -->
                    <div class="review-section mb-4">
                      <h5 class="section-title mb-3">Customer Information</h5>
                      <div class="section-content">
                        <div class="row">
                          <div class="col-md-6">
                            <strong>Customer Name:</strong>
                            {{ customerInfoData()?.customerName || 'Not provided' }}
                          </div>
                          <div class="col-md-6">
                            <strong>Friendly Name:</strong>
                            {{ customerInfoData()?.friendlyName || 'Not provided' }}
                          </div>
                          <div class="col-md-6">
                            <strong>Preferred Go Live Date:</strong>
                            {{
                              customerInfoData()?.preferredGoLiveDate
                                ? (customerInfoData()?.preferredGoLiveDate | date)
                                : 'Not provided'
                            }}
                          </div>
                          <div class="col-md-6">
                            <strong>SLA Level:</strong>
                            {{ customerInfoData()?.slaLevel || 'Not provided' }}
                          </div>
                          <div class="col-md-12">
                            <strong>Selected Services:</strong>
                            {{ getSelectedServices().join(', ') || 'No services selected' }}
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Service Configurations Summary -->
                    @for (service of getSelectedServices(); track service) {
                      <div class="review-section mb-4">
                        <h5 class="section-title mb-3">{{ getServiceDisplayName(service) }} Configuration</h5>
                        <div class="section-content">
                          <pre>{{ getServiceData(service) | json }}</pre>
                        </div>
                      </div>
                    }

                    <!-- Incident Handling Summary -->
                    <div class="review-section mb-4">
                      <h5 class="section-title mb-3">Incident Handling</h5>
                      <div class="section-content">
                        <div class="row">
                          <div class="col-md-6">
                            <strong>Incident Handling Email:</strong>
                            {{ formData().incidentHandlingData?.incidentHandlingEmail || 'Not provided' }}
                          </div>
                          <div class="col-md-6">
                            <strong>IR Contacts:</strong>
                            {{ formData().incidentHandlingData?.irContacts?.length || 0 }} contact(s)
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            }
          </div>

          <!-- Navigation Buttons -->
          <div class="d-flex gap-3 mt-3 flex-shrink-0">
            <button
              kendoButton
              themeColor="tertiary"
              (click)="onCancel()"
            >
              Cancel
            </button>
            <button
              kendoButton
              themeColor="secondary"
              [disabled]="currentStepIndex() === 0"
              (click)="onPrevious()"
            >
              Previous
            </button>

            <div>
              @if (currentStep() !== 'review-submit') {
                <button
                  kendoButton
                  themeColor="primary"
                  [disabled]="!isCurrentStepValid()"
                  (click)="onNext()"
                >
                  Next
                </button>
              } @else {
                <button
                  kendoButton
                  themeColor="success"
                  [disabled]="!isFormValid()"
                  (click)="onSubmit()"
                >
                  Submit Onboarding
                </button>
              }
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
